// PACKAGE #10 — Cvanto Balance / Coins Foundation.
// PACKAGE #12 Phase E (2026-09-02) — premium Wallet/Payments surface:
// animated balance, saved-card row (masked PAN only, never raw card
// data), active-subscription card wired to the real cancel-auto-renew
// endpoint (server/routes/subscription.js POST /cancel-renewal, built in
// Phase D but never surfaced in any UI until now), and richer transaction
// history. Server remains the sole source of truth throughout — every
// number here is a direct read of WalletContext/GET /api/subscription/me/
// GET /api/wallet/transactions, never a locally-mutated optimistic value.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  Crown,
  Gift,
  Inbox,
  ArrowDownLeft,
  ArrowUpRight,
  RotateCcw,
  SlidersHorizontal,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useWallet } from "../../../contexts/WalletContext";
import { getAccessToken } from "../../../awsAuth";
import TopUpSheet from "./TopUpSheet";
import logoWhite from "../../Header/img/logo-white.png";
import "./Balance.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const TYPE_LABELS = {
  topup: "Поповнення",
  debit: "Списання",
  refund: "Повернення",
  bonus: "Бонус",
  adjustment: "Коригування",
};

const TYPE_ICONS = {
  topup: ArrowDownLeft,
  debit: ArrowUpRight,
  refund: RotateCcw,
  bonus: Gift,
  adjustment: SlidersHorizontal,
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatBillingDate(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString("uk-UA", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// Small, self-contained ease-out count-up — no animation library in this
// project (checked package.json), and this is the one place a raw number
// jump would actually read as un-premium. Jumps instantly for
// prefers-reduced-motion or when going from "no value yet" to a value
// (nothing to animate FROM on first paint).
function useAnimatedNumber(value) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (prefersReduced || prevRef.current == null || value == null) {
      prevRef.current = value;
      setDisplay(value);
      return undefined;
    }

    const from = prevRef.current;
    const to = value;
    if (from === to) return undefined;

    const duration = 600;
    const start = performance.now();
    const easeOutCubic = (t) => 1 - (1 - t) ** 3;

    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(t);
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        prevRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return display;
}

function BalanceSkeleton() {
  return (
    <div className="walletSkeleton" aria-hidden="true">
      <div className="walletSkeleton__card" />
      <div className="walletSkeleton__row" />
      <div className="walletSkeleton__row" />
      <div className="walletSkeleton__row" />
    </div>
  );
}

export default function Balance() {
  const navigate = useNavigate();
  const { isLoggedIn, userId } = useAuth();
  const { balance, loading: balanceLoading, refresh } = useWallet();
  const animatedBalance = useAnimatedNumber(balanceLoading ? null : balance);

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [topUpOpen, setTopUpOpen] = useState(false);

  const [billing, setBilling] = useState(null); // GET /api/subscription/me's `billing` object, or null
  const [billingLoading, setBillingLoading] = useState(true);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const loadHistory = useCallback(async (nextCursor) => {
    try {
      if (nextCursor) setLoadingMore(true);
      else setHistoryLoading(true);
      setError("");

      const token = await getAccessToken().catch(() => null);
      const qs = nextCursor ? `?cursor=${encodeURIComponent(nextCursor)}` : "";

      const res = await fetch(`${API_BASE}/api/wallet/transactions${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      setItems((prev) => (nextCursor ? [...prev, ...(data.items || [])] : data.items || []));
      setCursor(data.nextCursor || null);
    } catch (e) {
      setError("Не вдалося завантажити історію");
    } finally {
      setHistoryLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadBilling = useCallback(async () => {
    try {
      setBillingLoading(true);
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/subscription/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBilling(data?.billing || null);
    } catch (e) {
      setBilling(null);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && userId) {
      loadHistory(null);
      loadBilling();
    }
  }, [isLoggedIn, userId, loadHistory, loadBilling]);

  const handleTopUpClose = () => {
    setTopUpOpen(false);
    refresh();
    loadHistory(null);
  };

  const handleCancelRenewal = async () => {
    if (cancelBusy) return;
    const ok = window.confirm(
      "Скасувати автопродовження? Доступ до тарифу залишиться активним до кінця поточного оплаченого періоду, повторне списання не відбудеться."
    );
    if (!ok) return;

    setCancelBusy(true);
    setCancelError("");
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/subscription/cancel-renewal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadBilling();
    } catch (e) {
      setCancelError("Не вдалося скасувати автопродовження. Спробуйте ще раз.");
    } finally {
      setCancelBusy(false);
    }
  };

  const planLabel = useMemo(() => {
    if (!billing?.plan) return "";
    return billing.plan === "business" ? "Cvanto Business" : "Cvanto Plus";
  }, [billing]);

  const showSubscriptionCard = !billingLoading && billing && billing.plan && billing.plan !== "free";
  const showSkeleton = balanceLoading && historyLoading && billingLoading && items.length === 0;

  return (
    <main className="walletPage">
      <header className="walletPage__hero">
        <button
          type="button"
          className="walletPage__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <h1>Гаманець</h1>
      </header>

      {showSkeleton ? (
        <BalanceSkeleton />
      ) : (
        <>
          <section className="walletHero">
            <div className="walletHero__card">
              <div className="walletHero__top">
                <img src={logoWhite} alt="" className="walletHero__logo" />
                <span className="walletHero__label">Wallet</span>
              </div>

              <div className="walletHero__amount">
                {balanceLoading ? (
                  <span className="walletHero__amountSkeleton" />
                ) : (
                  <>
                    {animatedBalance}
                    <span className="walletHero__currency">Tokens</span>
                  </>
                )}
              </div>

              <div className="walletHero__rate">1 Token = 1 гривня</div>
            </div>

            <button
              type="button"
              className="walletHero__topup"
              onClick={() => setTopUpOpen(true)}
            >
              Поповнити
            </button>
          </section>

          {(billing?.maskedPan || showSubscriptionCard) && (
            <section className="walletGroup">
              {billing?.maskedPan && (
                <div className="walletGroup__row">
                  <span className="walletGroup__icon">
                    <CreditCard size={17} strokeWidth={2} />
                  </span>
                  <span className="walletGroup__rowLabel">Картка •••• {String(billing.maskedPan).slice(-4)}</span>
                </div>
              )}

              {showSubscriptionCard && (
                <div className="walletGroup__row walletGroup__row--subscription">
                  <span className="walletGroup__icon">
                    <Crown size={17} strokeWidth={2} />
                  </span>
                  <div className="walletGroup__rowMain">
                    <div className="walletGroup__rowTop">
                      <span className="walletGroup__rowLabel">{planLabel}</span>
                      {billing.billingStatus === "past_due" && (
                        <span className="walletGroup__status is-warning">Проблема з оплатою</span>
                      )}
                      {billing.billingStatus === "canceled" && (
                        <span className="walletGroup__status">Вимкнено</span>
                      )}
                    </div>

                    <p className="walletGroup__rowSub">
                      {billing.autoRenew && billing.nextBillingAt ? (
                        <>
                          Наступне списання {formatBillingDate(billing.nextBillingAt)}
                          {billing.pendingPlanChange && (
                            <> · перейде на {billing.pendingPlanChange === "business" ? "Business" : "Plus"}</>
                          )}
                        </>
                      ) : billing.cancellationAtPeriodEnd ? (
                        "Доступ активний до кінця оплаченого періоду"
                      ) : (
                        "Разова оплата, без автопродовження"
                      )}
                    </p>

                    {billing.autoRenew && !billing.cancellationAtPeriodEnd && (
                      <button
                        type="button"
                        className="walletGroup__cancel"
                        onClick={handleCancelRenewal}
                        disabled={cancelBusy}
                      >
                        {cancelBusy ? "Скасовуємо…" : "Скасувати автопродовження"}
                      </button>
                    )}

                    {cancelError && <div className="walletGroup__error">{cancelError}</div>}
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="walletHistory">
            <h2>Історія операцій</h2>

            {historyLoading ? (
              <div className="walletHistory__state">Завантаження...</div>
            ) : error ? (
              <div className="walletHistory__state walletHistory__state--error">
                <AlertCircle size={18} strokeWidth={2} />
                {error}
              </div>
            ) : items.length === 0 ? (
              <div className="walletHistory__empty">
                <Inbox size={28} strokeWidth={1.6} />
                <p>Операцій ще немає</p>
                <span>Поповніть гаманець, щоб піднімати оголошення та відкривати більше можливостей</span>
              </div>
            ) : (
              <ul className="walletHistory__list">
                {items.map((tx, index) => {
                  const Icon = TYPE_ICONS[tx.type] || ArrowDownLeft;
                  return (
                    <li
                      key={tx.transactionId}
                      className="walletHistory__item"
                      style={{ "--wallet-item-delay": `${Math.min(index, 8) * 40}ms` }}
                    >
                      <span className={`walletHistory__itemIcon is-${tx.direction}`}>
                        <Icon size={16} strokeWidth={2.3} />
                      </span>

                      <div className="walletHistory__item-main">
                        <span className="walletHistory__item-label">
                          {tx.description || TYPE_LABELS[tx.type] || tx.type}
                        </span>
                        <span className="walletHistory__item-date">{formatDate(tx.createdAt)}</span>
                      </div>

                      <span
                        className={`walletHistory__item-amount ${
                          tx.direction === "credit" ? "is-credit" : "is-debit"
                        }`}
                      >
                        {tx.direction === "credit" ? "+" : "−"}
                        {tx.amount}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {cursor && !historyLoading && (
              <button
                type="button"
                className="walletHistory__more"
                onClick={() => loadHistory(cursor)}
                disabled={loadingMore}
              >
                {loadingMore ? "Завантаження..." : "Показати ще"}
              </button>
            )}
          </section>
        </>
      )}

      {topUpOpen && <TopUpSheet balance={balance} onClose={handleTopUpClose} />}
    </main>
  );
}
